/**
 * Elite Batch 13 Part 1 — Workflow Templates 1148–1167
 * Reddit community management, referral program automation, loyalty redemption,
 * compliance re-consent, repermission campaigns, resubscriber onboarding,
 * retargeting video ads, and review collection bots.
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function hubspotGetNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "GET",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [],
        variables: vars,
      },
    },
    x,
  );
}
function hubspotPostNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  bodyFields: Array<{ key: string; value: string }>,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyFields,
        variables: vars,
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 13 PART 1 — TEMPLATES 1148–1167
// =============================================================================

export const ELITE_BATCH13_PART1: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 1148. REDDIT COMMENT DRAFT QUEUE (8 nodes)
  // ===================================================================

  "Reddit Comment Draft Queue": {
    summary:
      "Scheduled poll discovers Reddit brand mentions via the Reddit API, AI filters for relevance, drafts contextual comments, checks compliance, routes through human approval, posts approved comments back to Reddit, and logs all activity to Google Sheets.",
    nodes: [
      trigger(
        "Schedule: Poll Reddit for brand mentions every 2 hours",
        "schedule",
        {
          schedule: "0 */2 * * * (Every 2 hours)",
          howItStarts:
            "A recurring schedule polls Reddit for new posts and comments mentioning your brand, feeding them into an AI-powered comment drafting pipeline",
          setup:
            "Configure Reddit API credentials (client_id, client_secret, refresh_token) with read and submit scopes. Provide OpenAI API key. Set up Google Sheets spreadsheet ID for activity logging. Define brand keywords for mention detection.",
          inputFields: [],
        },
      ),

      // 2. Poll Reddit API for brand mentions
      n("2", "hitApi", "Poll Reddit API for brand mentions", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://oauth.reddit.com/search?q={brand_keywords}&sort=new&t=hour&limit=25&type=link,comment",
          headers: [
            { key: "Authorization", value: "Bearer {reddit_access_token}" },
            { key: "User-Agent", value: "{app_name}/1.0" },
          ],
          body: [],
          variables: [
            {
              name: "data.children",
              customName: "mentions_json",
              type: "string",
            },
            { name: "data.dist", customName: "mention_count", type: "number" },
          ],
        },
      }),

      // 3. AI relevance filter and comment drafter
      aiNode(
        "3",
        "AI: Filter relevance and draft comments",
        520,
        "You are a Reddit community engagement specialist. Analyze brand mentions for relevance, filter out spam/irrelevant posts, and draft helpful, authentic Reddit-style comments. Never be salesy. Match the tone and language conventions of each subreddit. Return JSON.",
        "Brand mentions from Reddit: {mentions_json}. Total found: {mention_count}. For each relevant mention: assess relevance_score (0-100), draft a helpful comment that adds value to the conversation, note the subreddit rules context. Return relevant_count (number of mentions scoring above 60), drafted_comments (array of objects with post_id, subreddit, comment_text, relevance_score), compliance_flags (any comments that reference competitors or make claims needing review, or 'none' if all clean).",
        [
          { field: "relevant_count", as: "relevant_count", type: "number" },
          {
            field: "drafted_comments",
            as: "drafted_comments_json",
            type: "string",
          },
          { field: "compliance_flags", as: "compliance_flags", type: "string" },
        ],
      ),

      // 4. Compliance scanner condition
      n("4", "condition", "Are all drafted comments compliance-safe?", 760, {
        conditionData: [
          {
            variable: "compliance_flags",
            value: "none",
            comparison_operator: "EQUALS",
            logical_operator: "END",
          },
        ],
      }),

      // 5. Human approval gate
      n("5", "approvalGate", "Manager approves Reddit comment batch", 1000, {
        approvalPrompt:
          "Review {relevant_count} drafted Reddit comments for brand mentions.\n\nCompliance flags: {compliance_flags}\n\nDrafted comments:\n{drafted_comments_json}\n\nApprove to post all comments, or reject to discard this batch.",
      }),

      // 6. Reddit API post comments
      n("6", "hitApi", "Post approved comments to Reddit", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://oauth.reddit.com/api/comment",
          headers: [
            { key: "Authorization", value: "Bearer {reddit_access_token}" },
            { key: "User-Agent", value: "{app_name}/1.0" },
            { key: "Content-Type", value: "application/x-www-form-urlencoded" },
          ],
          body: [
            { key: "thing_id", value: "{post_fullname}" },
            { key: "text", value: "{approved_comment_text}" },
          ],
          variables: [
            {
              name: "json.data.things[0].data.id",
              customName: "comment_id",
              type: "string",
            },
            {
              name: "json.data.things[0].data.name",
              customName: "comment_fullname",
              type: "string",
            },
          ],
        },
      }),

      // 7. Log to Sheets
      sheetsNode(
        "7",
        "Log Reddit engagement to Google Sheets",
        1480,
        "RedditComments",
        '[["{timestamp}","{mention_count}","{relevant_count}","{comment_id}","{post_fullname}","posted","approved"]]',
      ),

      // 8. Slack notification on compliance issues
      slackNode(
        "8",
        "Alert Slack on compliance-flagged comments",
        1000,
        ":rotating_light: *Reddit Comment Compliance Alert*\nFlagged comments found: {compliance_flags}\nTotal mentions: {mention_count}\nRelevant: {relevant_count}\nPlease review flagged drafts before manual posting.",
        400,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "8", "output_2"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1149. REDDIT COMMUNITY DRAFT QUEUE (9 nodes)
  // ===================================================================

  "Reddit Community Draft Queue": {
    summary:
      "Manual trigger accepts a content brief, fetches target subreddit rules from Reddit API, AI generates a rule-compliant draft, validates policy compliance and subreddit-specific rules, notifies on failures, routes through human approval, publishes to Reddit, and tracks performance in Sheets.",
    nodes: [
      trigger("Manual: Submit Reddit content brief", "manual", {
        howItStarts:
          "A team member submits a content brief describing the topic, target subreddit, and goals for a new Reddit post",
        setup:
          "Configure Reddit API credentials with submit scope. Provide OpenAI API key. Set up Google Sheets for performance tracking and email for failure notifications.",
        inputFields: [
          { name: "target_subreddit", type: "string", example: "r/saas" },
          {
            name: "content_topic",
            type: "string",
            example: "How we reduced churn by 40% with automated onboarding",
          },
          { name: "post_type", type: "string", example: "text" },
          {
            name: "brand_guidelines",
            type: "string",
            example: "No direct product plugs, value-first approach",
          },
        ],
      }),

      // 2. Fetch subreddit rules
      n("2", "hitApi", "Fetch subreddit rules from Reddit API", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://oauth.reddit.com/r/{target_subreddit}/about/rules",
          headers: [
            { key: "Authorization", value: "Bearer {reddit_access_token}" },
            { key: "User-Agent", value: "{app_name}/1.0" },
          ],
          body: [],
          variables: [
            {
              name: "rules",
              customName: "subreddit_rules_json",
              type: "string",
            },
            {
              name: "site_rules",
              customName: "site_rules_json",
              type: "string",
            },
          ],
        },
      }),

      // 3. AI draft generator
      aiNode(
        "3",
        "AI: Generate subreddit-compliant post draft",
        520,
        "You are a Reddit content strategist. Create posts that read as genuine community contributions, not marketing. Follow all subreddit rules exactly. Use appropriate formatting (headers, bullet points) for the subreddit style. Never include obvious self-promotion. Return JSON.",
        "Content brief: Topic: {content_topic}. Subreddit: {target_subreddit}. Post type: {post_type}. Brand guidelines: {brand_guidelines}. Subreddit rules: {subreddit_rules_json}. Generate post_title (compelling, follows subreddit title conventions), post_body (markdown-formatted, value-driven content that follows all rules), flair_suggestion (if subreddit requires flair), policy_compliant (true/false), rule_violations (list any rule conflicts or 'none'), quality_score (1-100 based on likely engagement).",
        [
          { field: "post_title", as: "draft_title", type: "string" },
          { field: "post_body", as: "draft_body", type: "string" },
          { field: "flair_suggestion", as: "suggested_flair", type: "string" },
          {
            field: "policy_compliant",
            as: "policy_compliant",
            type: "boolean",
          },
          { field: "rule_violations", as: "rule_violations", type: "string" },
          { field: "quality_score", as: "quality_score", type: "number" },
        ],
      ),

      // 4. Policy compliance condition
      n("4", "condition", "Does post pass internal policy compliance?", 760, {
        conditionData: [
          {
            variable: "policy_compliant",
            value: "true",
            comparison_operator: "EQUALS",
            logical_operator: "END",
          },
        ],
      }),

      // 5. Subreddit rule validator condition
      n("5", "condition", "Does post pass all subreddit rules?", 1000, {
        conditionData: [
          {
            variable: "rule_violations",
            value: "none",
            comparison_operator: "EQUALS",
            logical_operator: "END",
          },
        ],
      }),

      // 6. Failure notification email
      emailNode(
        "6",
        "Notify team of draft failure",
        1000,
        "{team_email}",
        "Reddit Draft Failed Validation - {target_subreddit}",
        "<h2>Draft Validation Failed</h2><p>The AI-generated draft for r/{target_subreddit} failed compliance checks.</p><p><strong>Topic:</strong> {content_topic}</p><p><strong>Policy Compliant:</strong> {policy_compliant}</p><p><strong>Rule Violations:</strong> {rule_violations}</p><p><strong>Quality Score:</strong> {quality_score}/100</p><p>Please revise the content brief and resubmit.</p>",
        400,
      ),

      // 7. Human approval gate
      n("7", "approvalGate", "Editor approves Reddit post draft", 1240, {
        approvalPrompt:
          "Review Reddit post draft for r/{target_subreddit}:\n\nTitle: {draft_title}\nFlair: {suggested_flair}\nQuality Score: {quality_score}/100\n\nBody preview:\n{draft_body}\n\nApprove to publish, reject to discard.",
      }),

      // 8. Reddit API publish
      n("8", "hitApi", "Publish approved post to Reddit", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://oauth.reddit.com/api/submit",
          headers: [
            { key: "Authorization", value: "Bearer {reddit_access_token}" },
            { key: "User-Agent", value: "{app_name}/1.0" },
            { key: "Content-Type", value: "application/x-www-form-urlencoded" },
          ],
          body: [
            { key: "sr", value: "{target_subreddit}" },
            { key: "kind", value: "self" },
            { key: "title", value: "{draft_title}" },
            { key: "text", value: "{draft_body}" },
            { key: "flair_text", value: "{suggested_flair}" },
          ],
          variables: [
            { name: "json.data.url", customName: "post_url", type: "string" },
            { name: "json.data.id", customName: "post_id", type: "string" },
            {
              name: "json.data.name",
              customName: "post_fullname",
              type: "string",
            },
          ],
        },
      }),

      // 9. Performance tracker Sheets
      sheetsNode(
        "9",
        "Log post to performance tracker",
        1720,
        "RedditPosts",
        '[["{timestamp}","{target_subreddit}","{draft_title}","{post_url}","{quality_score}","{suggested_flair}","published"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "7", "output_1"),
      e("5", "6", "output_2"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1150. REDDIT ENGAGEMENT AUTOPILOT (10 nodes)
  // ===================================================================

  "Reddit Engagement Autopilot": {
    summary:
      "Scheduled polling discovers trending Reddit threads, scores each thread for engagement potential via runCode, filters top candidates, generates authentic replies, checks for duplicates and compliance, routes through human approval, batch-posts approved replies, and logs analytics to Sheets.",
    nodes: [
      trigger(
        "Schedule: Poll trending Reddit threads every 4 hours",
        "schedule",
        {
          schedule: "0 */4 * * * (Every 4 hours)",
          howItStarts:
            "A recurring schedule polls target subreddits for trending threads, scoring them for engagement opportunity and generating context-aware replies",
          setup:
            "Configure Reddit API credentials with read and submit scopes. Provide OpenAI API key. Set up Google Sheets for analytics. Define target subreddits and engagement criteria.",
          inputFields: [],
        },
      ),

      // 2. Reddit API poll trending threads
      n("2", "hitApi", "Poll trending threads from target subreddits", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://oauth.reddit.com/r/{target_subreddits}/hot?limit=50",
          headers: [
            { key: "Authorization", value: "Bearer {reddit_access_token}" },
            { key: "User-Agent", value: "{app_name}/1.0" },
          ],
          body: [],
          variables: [
            {
              name: "data.children",
              customName: "threads_json",
              type: "string",
            },
            { name: "data.dist", customName: "thread_count", type: "number" },
          ],
        },
      }),

      // 3. Thread scoring runCode
      n("3", "runCode", "Score threads for engagement potential", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `const threads = JSON.parse(vars.threads_json || "[]");\nconst scored = threads.map(t => {\n  const d = t.data || t;\n  let score = 0;\n  const ageHrs = (Date.now() / 1000 - (d.created_utc || 0)) / 3600;\n  if (ageHrs < 2) score += 30;\n  else if (ageHrs < 6) score += 20;\n  else if (ageHrs < 12) score += 10;\n  if (d.num_comments > 5 && d.num_comments < 100) score += 25;\n  else if (d.num_comments <= 5) score += 15;\n  if (d.upvote_ratio > 0.85) score += 20;\n  if (!d.locked && !d.removed_by_category) score += 15;\n  if ((d.title || "").includes("?")) score += 10;\n  return { id: d.id, title: d.title, subreddit: d.subreddit, score, num_comments: d.num_comments, fullname: d.name };\n}).sort((a, b) => b.score - a.score);\nconst top = scored.filter(s => s.score >= 50).slice(0, 10);\nreturn { scored_threads_json: JSON.stringify(top), top_thread_count: top.length, highest_score: top.length > 0 ? top[0].score : 0 };`,
          outputVariables: [
            { name: "scored_threads_json", type: "string" },
            { name: "top_thread_count", type: "number" },
            { name: "highest_score", type: "number" },
          ],
        },
      }),

      // 4. Top thread selector condition
      n(
        "4",
        "condition",
        "Are there high-scoring threads to engage with?",
        760,
        {
          conditionData: [
            {
              variable: "top_thread_count",
              value: "0",
              comparison_operator: "GREATER THAN",
              logical_operator: "END",
            },
          ],
        },
      ),

      // 5. AI reply generator
      aiNode(
        "5",
        "AI: Generate authentic thread replies",
        1000,
        "You are a Reddit community member who genuinely helps people. Write replies that are authentic, add real value, share specific insights or experiences, and never sound like marketing copy. Match the tone of each subreddit. Return JSON.",
        "Top-scoring threads to engage with: {scored_threads_json}. For each thread, generate a helpful reply. Return replies (array with thread_id, thread_fullname, subreddit, reply_text, is_duplicate_risk boolean), total_replies (number), compliance_clean (true if all replies pass brand safety).",
        [
          { field: "replies", as: "generated_replies_json", type: "string" },
          { field: "total_replies", as: "total_replies", type: "number" },
          {
            field: "compliance_clean",
            as: "compliance_clean",
            type: "boolean",
          },
        ],
      ),

      // 6. Duplicate check condition
      n("6", "condition", "Are all replies unique (no duplicate risk)?", 1240, {
        conditionData: [
          {
            variable: "compliance_clean",
            value: "true",
            comparison_operator: "EQUALS",
            logical_operator: "END",
          },
        ],
      }),

      // 7. Compliance validator condition
      n("7", "condition", "Do all replies pass compliance validation?", 1480, {
        conditionData: [
          {
            variable: "total_replies",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "END",
          },
        ],
      }),

      // 8. Human approval gate
      n("8", "approvalGate", "Community manager approves reply batch", 1720, {
        approvalPrompt:
          "Review {total_replies} AI-generated Reddit replies:\n\n{generated_replies_json}\n\nHighest thread score: {highest_score}\nCompliance clean: {compliance_clean}\n\nApprove to post all replies, or reject to discard.",
      }),

      // 9. Reddit API batch poster
      n("9", "hitApi", "Batch post approved replies to Reddit", 1960, {
        hitApiDetails: {
          method: "POST",
          url: "https://oauth.reddit.com/api/comment",
          headers: [
            { key: "Authorization", value: "Bearer {reddit_access_token}" },
            { key: "User-Agent", value: "{app_name}/1.0" },
            { key: "Content-Type", value: "application/x-www-form-urlencoded" },
          ],
          body: [
            { key: "thing_id", value: "{thread_fullname}" },
            { key: "text", value: "{reply_text}" },
          ],
          variables: [
            {
              name: "json.data.things[0].data.id",
              customName: "reply_id",
              type: "string",
            },
            {
              name: "json.data.things[0].data.name",
              customName: "reply_fullname",
              type: "string",
            },
          ],
        },
      }),

      // 10. Analytics Sheets logger
      sheetsNode(
        "10",
        "Log engagement analytics to Sheets",
        2200,
        "RedditEngagement",
        '[["{timestamp}","{top_thread_count}","{total_replies}","{highest_score}","{reply_id}","posted","autopilot"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "10", "output_2"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "10", "output_2"),
      e("7", "8", "output_1"),
      e("7", "10", "output_2"),
      e("8", "9"),
      e("9", "10"),
    ],
  },

  // ===================================================================
  // 1151. REDDIT POST DRAFT QUEUE (8 nodes)
  // ===================================================================

  "Reddit Post Draft Queue": {
    summary:
      "Manual trigger initiates research by fetching top-performing posts from Reddit SERP, AI drafts a competitive post, validates policy compliance and quality scoring, routes through human approval, publishes to Reddit, and sends a post-publish Slack notification for monitoring.",
    nodes: [
      trigger("Manual: Create research-driven Reddit post", "manual", {
        howItStarts:
          "A team member initiates creation of a new Reddit post by specifying a topic and target subreddit, triggering competitive research and AI drafting",
        setup:
          "Configure Reddit API credentials with read and submit scopes. Provide OpenAI API key. Set up Slack webhook for post-publish monitoring notifications.",
        inputFields: [
          { name: "target_subreddit", type: "string", example: "r/marketing" },
          {
            name: "topic_keywords",
            type: "string",
            example: "email marketing deliverability tips",
          },
          {
            name: "post_goal",
            type: "string",
            example: "Establish thought leadership with actionable tips",
          },
        ],
      }),

      // 2. Reddit API fetch top posts for research
      n(
        "2",
        "hitApi",
        "Fetch top-performing posts for competitive research",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://oauth.reddit.com/r/{target_subreddit}/search?q={topic_keywords}&sort=top&t=month&limit=15",
            headers: [
              { key: "Authorization", value: "Bearer {reddit_access_token}" },
              { key: "User-Agent", value: "{app_name}/1.0" },
            ],
            body: [],
            variables: [
              {
                name: "data.children",
                customName: "top_posts_json",
                type: "string",
              },
              {
                name: "data.dist",
                customName: "research_count",
                type: "number",
              },
            ],
          },
        },
      ),

      // 3. AI post drafter
      aiNode(
        "3",
        "AI: Draft competitive Reddit post",
        520,
        "You are an expert Reddit content creator. Analyze top-performing posts in this subreddit to understand what resonates, then draft a post that outperforms them. Use formatting that works on Reddit (headers, bullet points, bold). Write in first person with genuine expertise. Never sound like AI or marketing. Return JSON.",
        "Research data - top posts in r/{target_subreddit}: {top_posts_json}. Post goal: {post_goal}. Topic: {topic_keywords}. Analyze patterns in successful posts. Generate draft_title (attention-grabbing, follows subreddit conventions), draft_body (markdown, actionable, 300-800 words), policy_compliant (true/false), quality_score (1-100), competitive_angle (what makes this post unique).",
        [
          { field: "draft_title", as: "draft_title", type: "string" },
          { field: "draft_body", as: "draft_body", type: "string" },
          {
            field: "policy_compliant",
            as: "policy_compliant",
            type: "boolean",
          },
          { field: "quality_score", as: "quality_score", type: "number" },
          {
            field: "competitive_angle",
            as: "competitive_angle",
            type: "string",
          },
        ],
      ),

      // 4. Policy compliance condition
      n("4", "condition", "Does draft pass policy compliance?", 760, {
        conditionData: [
          {
            variable: "policy_compliant",
            value: "true",
            comparison_operator: "EQUALS",
            logical_operator: "END",
          },
        ],
      }),

      // 5. Quality score condition
      n(
        "5",
        "condition",
        "Does draft meet quality threshold (score >= 70)?",
        1000,
        {
          conditionData: [
            {
              variable: "quality_score",
              value: "70",
              comparison_operator: "GREATER THAN OR EQUALS",
              logical_operator: "END",
            },
          ],
        },
      ),

      // 6. Human approval gate
      n("6", "approvalGate", "Content lead approves Reddit post", 1240, {
        approvalPrompt:
          "Review Reddit post for r/{target_subreddit}:\n\nTitle: {draft_title}\nQuality Score: {quality_score}/100\nCompetitive Angle: {competitive_angle}\n\nBody:\n{draft_body}\n\nApprove to publish, reject to discard.",
      }),

      // 7. Reddit API publish
      n("7", "hitApi", "Publish approved post to Reddit", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://oauth.reddit.com/api/submit",
          headers: [
            { key: "Authorization", value: "Bearer {reddit_access_token}" },
            { key: "User-Agent", value: "{app_name}/1.0" },
            { key: "Content-Type", value: "application/x-www-form-urlencoded" },
          ],
          body: [
            { key: "sr", value: "{target_subreddit}" },
            { key: "kind", value: "self" },
            { key: "title", value: "{draft_title}" },
            { key: "text", value: "{draft_body}" },
          ],
          variables: [
            { name: "json.data.url", customName: "post_url", type: "string" },
            { name: "json.data.id", customName: "post_id", type: "string" },
          ],
        },
      }),

      // 8. Post-publish monitor Slack
      slackNode(
        "8",
        "Notify Slack for post-publish monitoring",
        1720,
        ":memo: *New Reddit Post Published*\nSubreddit: r/{target_subreddit}\nTitle: {draft_title}\nQuality Score: {quality_score}/100\nURL: {post_url}\nAngle: {competitive_angle}\nMonitor engagement over the next 4 hours.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "8", "output_2"),
      e("5", "6", "output_1"),
      e("5", "8", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1152. REDEEM-POINTS PROMPT (7 nodes)
  // ===================================================================

  "Redeem-Points Prompt": {
    summary:
      "Daily schedule fetches loyalty members with redeemable point balances from HubSpot, matches available rewards via runCode, AI generates personalized redemption messages, routes by channel preference, dispatches email notifications, and tracks engagement in Sheets.",
    nodes: [
      trigger("Schedule: Daily loyalty redemption prompts", "schedule", {
        schedule: "0 10 * * * (Daily at 10 AM)",
        howItStarts:
          "A daily schedule identifies loyalty program members with sufficient points for redemption and sends personalized prompts encouraging them to use their rewards",
        setup:
          "Configure HubSpot API key with contacts read access and custom properties: loyalty_points_balance, loyalty_tier, preferred_channel, last_redemption_date. Provide OpenAI API key. Set up Google Sheets for engagement tracking.",
        inputFields: [],
      }),

      // 2. HubSpot fetch loyalty members
      hubspotGetNode(
        "2",
        "Fetch loyalty members with redeemable points",
        280,
        'crm/v3/objects/contacts?properties=email,firstname,phone,loyalty_points_balance,loyalty_tier,preferred_channel,last_redemption_date&filterGroups=[{"filters":[{"propertyName":"loyalty_points_balance","operator":"GTE","value":"100"}]}]&limit=50',
        [
          {
            name: "results",
            customName: "eligible_members_json",
            type: "string",
          },
          { name: "total", customName: "eligible_count", type: "number" },
        ],
      ),

      // 3. Reward catalog matcher runCode
      n("3", "runCode", "Match members to available reward tiers", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `const members = JSON.parse(vars.eligible_members_json || "[]");\nconst rewards = [\n  { tier: "Bronze", min_points: 100, options: ["$5 gift card", "Free shipping voucher", "10% discount code"] },\n  { tier: "Silver", min_points: 500, options: ["$25 gift card", "Premium feature unlock", "Exclusive webinar access"] },\n  { tier: "Gold", min_points: 1000, options: ["$50 gift card", "Annual subscription discount", "VIP event invitation"] },\n  { tier: "Platinum", min_points: 2500, options: ["$100 gift card", "Free product upgrade", "Personal account manager session"] }\n];\nconst matched = members.map(m => {\n  const props = m.properties || m;\n  const pts = parseInt(props.loyalty_points_balance) || 0;\n  const available = rewards.filter(r => pts >= r.min_points);\n  const bestTier = available.length > 0 ? available[available.length - 1] : rewards[0];\n  return { email: props.email, name: props.firstname || "Member", phone: props.phone || "", points: pts, matched_tier: bestTier.tier, reward_options: bestTier.options.join(", "), preferred_channel: props.preferred_channel || "email" };\n});\nconst emailCount = matched.filter(m => m.preferred_channel === "email").length;\nconst smsCount = matched.filter(m => m.preferred_channel === "sms").length;\nreturn { matched_members_json: JSON.stringify(matched), email_recipient_count: emailCount, sms_recipient_count: smsCount, total_matched: matched.length };`,
          outputVariables: [
            { name: "matched_members_json", type: "string" },
            { name: "email_recipient_count", type: "number" },
            { name: "sms_recipient_count", type: "number" },
            { name: "total_matched", type: "number" },
          ],
        },
      }),

      // 4. AI personalized message generator
      aiNode(
        "4",
        "AI: Generate personalized redemption messages",
        760,
        "You are a loyalty program engagement specialist. Create compelling, personalized redemption prompts that make members excited to use their points. Highlight the best reward options for their tier. Create urgency without being pushy. Return JSON.",
        "Eligible members matched to reward tiers: {matched_members_json}. Generate redemption_email_subject (personalized, highlights their tier), redemption_email_body (HTML with member name, point balance, top 3 reward options with descriptions, clear CTA button), redemption_sms (under 160 chars, friendly nudge with top reward mention and link).",
        [
          {
            field: "redemption_email_subject",
            as: "redemption_subject",
            type: "string",
          },
          {
            field: "redemption_email_body",
            as: "redemption_body_html",
            type: "string",
          },
          {
            field: "redemption_sms",
            as: "redemption_sms_text",
            type: "string",
          },
        ],
      ),

      // 5. Channel preference condition
      n("5", "condition", "Does member prefer email over SMS?", 1000, {
        conditionData: [
          {
            variable: "email_recipient_count",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "END",
          },
        ],
      }),

      // 6. Email dispatcher
      emailNode(
        "6",
        "Send redemption prompt email",
        1240,
        "{member_email}",
        "{redemption_subject}",
        "{redemption_body_html}",
      ),

      // 7. Engagement tracker Sheets
      sheetsNode(
        "7",
        "Log redemption prompt engagement",
        1480,
        "RedemptionPrompts",
        '[["{timestamp}","{eligible_count}","{total_matched}","{email_recipient_count}","{sms_recipient_count}","{redemption_subject}","sent"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1153. REFERRAL ADVOCACY EMAIL (8 nodes)
  // ===================================================================

  "Referral Advocacy Email": {
    summary:
      "Weekly schedule fetches power users from HubSpot, filters by satisfaction score, generates unique referral links via API, AI personalizes advocacy emails, sends via SendGrid, logs CRM activity in HubSpot, and schedules follow-up delays.",
    nodes: [
      trigger("Schedule: Weekly referral advocacy outreach", "schedule", {
        schedule: "0 10 * * 2 (Tuesday at 10 AM)",
        howItStarts:
          "A weekly schedule identifies power users with high satisfaction scores and sends them personalized referral advocacy emails with unique tracking links",
        setup:
          "Configure HubSpot API key with contacts read/write access and properties: nps_score, total_revenue, referral_count, hs_object_id. Provide referral platform API key. Configure SendGrid API key and verified sender. Provide OpenAI API key.",
        inputFields: [],
      }),

      // 2. HubSpot get power users
      hubspotGetNode(
        "2",
        "Fetch power users from HubSpot",
        280,
        'crm/v3/objects/contacts?properties=email,firstname,lastname,nps_score,total_revenue,referral_count,company,hs_object_id&filterGroups=[{"filters":[{"propertyName":"total_revenue","operator":"GTE","value":"500"},{"propertyName":"nps_score","operator":"GTE","value":"8"}]}]&limit=50',
        [
          { name: "results", customName: "power_users_json", type: "string" },
          { name: "total", customName: "power_user_count", type: "number" },
        ],
      ),

      // 3. Satisfaction score condition
      n(
        "3",
        "condition",
        "Are there power users with NPS >= 9 (promoters)?",
        520,
        {
          conditionData: [
            {
              variable: "power_user_count",
              value: "0",
              comparison_operator: "GREATER THAN",
              logical_operator: "END",
            },
          ],
        },
      ),

      // 4. Referral link generator API
      n("4", "hitApi", "Generate unique referral tracking links", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.referralcandy.com/v1/referral_links",
          headers: [
            { key: "Authorization", value: "Bearer {referral_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "advocate_email", value: "{advocate_email}" },
            { key: "campaign_id", value: "{referral_campaign_id}" },
            { key: "source", value: "advocacy_email" },
          ],
          variables: [
            {
              name: "referral_link",
              customName: "unique_referral_url",
              type: "string",
            },
            {
              name: "referral_code",
              customName: "referral_code",
              type: "string",
            },
            { name: "advocate_id", customName: "advocate_id", type: "string" },
          ],
        },
      }),

      // 5. AI email personalizer
      aiNode(
        "5",
        "AI: Personalize advocacy email content",
        1000,
        "You are a customer advocacy specialist. Write warm, genuine referral request emails that acknowledge the customer relationship, highlight mutual benefits, and make sharing easy. Avoid corporate jargon. Return JSON.",
        "Power user data: {power_users_json}. Referral link: {unique_referral_url}. Referral code: {referral_code}. Generate advocacy_subject (personal, not salesy), advocacy_body (HTML email with personalized greeting, acknowledgment of their success, clear benefit for them AND their referral, prominent referral link/code), preview_text (under 90 chars).",
        [
          { field: "advocacy_subject", as: "advocacy_subject", type: "string" },
          { field: "advocacy_body", as: "advocacy_body_html", type: "string" },
          { field: "preview_text", as: "preview_text", type: "string" },
        ],
      ),

      // 6. SendGrid email send
      n("6", "hitApi", "Send advocacy email via SendGrid", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.sendgrid.com/v3/mail/send",
          headers: [
            { key: "Authorization", value: "Bearer {sendgrid_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "personalizations",
              value: '[{"to":[{"email":"{advocate_email}"}]}]',
            },
            {
              key: "from",
              value: '{"email":"{sender_email}","name":"{sender_name}"}',
            },
            { key: "subject", value: "{advocacy_subject}" },
            {
              key: "content",
              value: '[{"type":"text/html","value":"{advocacy_body_html}"}]',
            },
          ],
          variables: [
            {
              name: "statusCode",
              customName: "sendgrid_status",
              type: "number",
            },
          ],
        },
      }),

      // 7. CRM activity logger HubSpot
      hubspotPostNode(
        "7",
        "Log advocacy activity in HubSpot CRM",
        1480,
        "crm/v3/objects/notes",
        [
          {
            key: "properties",
            value:
              '{"hs_note_body":"Referral advocacy email sent. Code: {referral_code}. Link: {unique_referral_url}","hs_timestamp":"{timestamp}"}',
          },
          {
            key: "associations",
            value:
              '[{"to":{"id":"{hs_object_id}"},"types":[{"associationCategory":"HUBSPOT_DEFINED","associationTypeId":202}]}]',
          },
        ],
        [{ name: "id", customName: "note_id", type: "string" }],
      ),

      // 8. Follow-up scheduler delay
      n("8", "delayAction", "Wait 5 days before follow-up check", 1720, {
        delayDetails: { duration: 5, unit: "days" },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("3", "8", "output_2"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1154. REFERRAL FRIEND PURCHASED THANK-YOU (7 nodes)
  // ===================================================================

  "Referral Friend Purchased Thank-You": {
    summary:
      "Webhook receives a purchase event, AI extracts purchase and referral data, looks up referral attribution via API, calculates rewards in runCode, sends thank-you email to referrer, confirmation email to friend, and updates both records in HubSpot CRM.",
    nodes: [
      trigger("Webhook: Friend purchase completed", "webhook_app", {
        source: "ecommerce",
        event: "order.completed",
        howItStarts:
          "An ecommerce webhook fires when a referred friend completes their first purchase, triggering a dual thank-you and reward fulfillment flow",
        setup:
          "Configure ecommerce platform webhook for order.completed events. Provide referral platform API key for attribution lookup. Configure OpenAI API key and HubSpot API key with contacts read/write.",
        inputFields: [],
      }),

      // 2. AI extract purchase data
      aiNode(
        "2",
        "AI: Extract purchase and referral data",
        280,
        "You are a data extraction specialist. Extract purchase details and referral attribution data from this webhook payload, regardless of the field naming format. Return JSON with standardized field names.",
        "Webhook payload: {webhook_payload}. Extract: buyer_email, buyer_name, order_id, order_total (number), currency, product_names (comma-separated list), referral_code (if present in UTM or discount code), purchase_date.",
        [
          { field: "buyer_email", as: "buyer_email", type: "string" },
          { field: "buyer_name", as: "buyer_name", type: "string" },
          { field: "order_id", as: "order_id", type: "string" },
          { field: "order_total", as: "order_total", type: "number" },
          { field: "product_names", as: "product_names", type: "string" },
          { field: "referral_code", as: "referral_code", type: "string" },
        ],
      ),

      // 3. Referral attribution lookup API
      n("3", "hitApi", "Look up referral attribution", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.referralcandy.com/v1/referrals?code={referral_code}",
          headers: [
            { key: "Authorization", value: "Bearer {referral_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "advocate.email",
              customName: "referrer_email",
              type: "string",
            },
            {
              name: "advocate.name",
              customName: "referrer_name",
              type: "string",
            },
            { name: "advocate.id", customName: "referrer_id", type: "string" },
            { name: "reward.type", customName: "reward_type", type: "string" },
          ],
        },
      }),

      // 4. Reward calculator runCode
      n("4", "runCode", "Calculate referral rewards for both parties", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `const orderTotal = parseFloat(vars.order_total) || 0;\nconst rewardType = vars.reward_type || "percentage";\nlet referrerReward = 0;\nlet friendDiscount = 0;\nif (rewardType === "percentage") {\n  referrerReward = Math.round(orderTotal * 0.15 * 100) / 100;\n  friendDiscount = Math.round(orderTotal * 0.10 * 100) / 100;\n} else {\n  referrerReward = 25;\n  friendDiscount = 10;\n}\nreturn {\n  referrer_reward_amount: referrerReward.toFixed(2),\n  friend_discount_amount: friendDiscount.toFixed(2),\n  referrer_summary: vars.referrer_name + " earned $" + referrerReward.toFixed(2) + " from " + vars.buyer_name + "'s purchase",\n  friend_summary: vars.buyer_name + " saved $" + friendDiscount.toFixed(2) + " thanks to " + vars.referrer_name,\n  reward_currency: "USD"\n};`,
          outputVariables: [
            { name: "referrer_reward_amount", type: "string" },
            { name: "friend_discount_amount", type: "string" },
            { name: "referrer_summary", type: "string" },
            { name: "friend_summary", type: "string" },
            { name: "reward_currency", type: "string" },
          ],
        },
      }),

      // 5. Referrer thank-you email
      emailNode(
        "5",
        "Send thank-you email to referrer",
        1000,
        "{referrer_email}",
        "Your referral just purchased! You earned ${referrer_reward_amount}",
        "<h2>Great news, {referrer_name}!</h2><p>Your friend <strong>{buyer_name}</strong> just completed their first purchase.</p><p><strong>Your reward:</strong> ${referrer_reward_amount} {reward_currency} credit added to your account.</p><p><strong>Order:</strong> {product_names} (${order_total})</p><p>{referrer_summary}</p><p>Keep sharing to earn more!</p>",
      ),

      // 6. Friend confirmation email
      emailNode(
        "6",
        "Send purchase confirmation to referred friend",
        1240,
        "{buyer_email}",
        "Welcome aboard, {buyer_name}! Order #{order_id} confirmed",
        "<h2>Thanks for joining us, {buyer_name}!</h2><p>Your order <strong>#{order_id}</strong> for <strong>{product_names}</strong> has been confirmed.</p><p>You saved <strong>${friend_discount_amount}</strong> thanks to {referrer_name}'s referral!</p><p>{friend_summary}</p><p>Share your own referral link and earn rewards when friends purchase.</p>",
        400,
      ),

      // 7. CRM dual update HubSpot
      hubspotPostNode(
        "7",
        "Update both contact records in HubSpot",
        1480,
        "crm/v3/objects/contacts/batch/update",
        [
          {
            key: "inputs",
            value:
              '[{"id":"{referrer_id}","properties":{"last_referral_reward":"{referrer_reward_amount}","last_referral_date":"{timestamp}"}},{"id":"{buyer_email}","idProperty":"email","properties":{"referred_by":"{referrer_email}","referral_discount_received":"{friend_discount_amount}","first_purchase_date":"{timestamp}"}}]',
          },
        ],
        [
          { name: "results", customName: "crm_update_results", type: "string" },
          { name: "status", customName: "crm_update_status", type: "string" },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("4", "6"),
      e("5", "7"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1155. REFERRAL PROGRAM BOT (9 nodes)
  // ===================================================================

  "Referral Program Bot": {
    summary:
      "Webhook receives chat messages, AI extracts user intent, checks authentication status, fetches referral data from API, AI generates conversational responses, routes by action type, provisions new referral links, escalates complex issues to Slack, and logs conversations to Sheets.",
    nodes: [
      trigger("Webhook: Chat message received", "webhook_app", {
        source: "chat_platform",
        event: "message.received",
        howItStarts:
          "A chat platform webhook fires when a user sends a message to the referral program bot, triggering intent detection and automated responses",
        setup:
          "Configure chat platform webhook (Intercom, Drift, or custom). Provide referral platform API key. Configure OpenAI API key, Slack webhook for escalations, and Google Sheets for conversation logging.",
        inputFields: [],
      }),

      // 2. AI extract intent
      aiNode(
        "2",
        "AI: Extract user intent from chat message",
        280,
        "You are an intent classification specialist for a referral program chatbot. Classify the user message into one of these intents: check_status, generate_link, redeem_reward, program_info, complaint, other. Extract any referral codes or email addresses mentioned. Return JSON.",
        "Chat message payload: {webhook_payload}. Extract: user_id, user_email (if mentioned), message_text, detected_intent (check_status|generate_link|redeem_reward|program_info|complaint|other), is_authenticated (true/false based on session data presence), confidence_score (0-100).",
        [
          { field: "user_id", as: "chat_user_id", type: "string" },
          { field: "user_email", as: "chat_user_email", type: "string" },
          { field: "message_text", as: "user_message", type: "string" },
          { field: "detected_intent", as: "detected_intent", type: "string" },
          {
            field: "is_authenticated",
            as: "is_authenticated",
            type: "boolean",
          },
          {
            field: "confidence_score",
            as: "intent_confidence",
            type: "number",
          },
        ],
      ),

      // 3. User auth check condition
      n("3", "condition", "Is the user authenticated?", 520, {
        conditionData: [
          {
            variable: "is_authenticated",
            value: "true",
            comparison_operator: "EQUALS",
            logical_operator: "END",
          },
        ],
      }),

      // 4. Referral data fetcher API
      n("4", "hitApi", "Fetch user referral program data", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.referralcandy.com/v1/advocates/{chat_user_id}/dashboard",
          headers: [
            { key: "Authorization", value: "Bearer {referral_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "total_referrals",
              customName: "total_referrals",
              type: "number",
            },
            {
              name: "pending_rewards",
              customName: "pending_rewards",
              type: "string",
            },
            {
              name: "earned_total",
              customName: "total_earned",
              type: "string",
            },
            {
              name: "referral_link",
              customName: "existing_referral_link",
              type: "string",
            },
            { name: "tier", customName: "advocate_tier", type: "string" },
          ],
        },
      }),

      // 5. AI conversational responder
      aiNode(
        "5",
        "AI: Generate conversational bot response",
        1000,
        "You are a friendly, helpful referral program chatbot. Respond naturally to the user based on their intent and account data. Be concise but thorough. If you cannot help, suggest escalation to a human agent. Return JSON.",
        "User intent: {detected_intent}. Message: {user_message}. Account: total_referrals={total_referrals}, pending_rewards={pending_rewards}, total_earned={total_earned}, referral_link={existing_referral_link}, tier={advocate_tier}. Generate bot_response (natural reply addressing their intent), action_needed (generate_link|escalate_human|none), requires_escalation (true/false).",
        [
          { field: "bot_response", as: "bot_response_text", type: "string" },
          { field: "action_needed", as: "action_needed", type: "string" },
          {
            field: "requires_escalation",
            as: "requires_escalation",
            type: "boolean",
          },
        ],
      ),

      // 6. Action router condition
      n(
        "6",
        "condition",
        "Does user need a new referral link generated?",
        1240,
        {
          conditionData: [
            {
              variable: "action_needed",
              value: "generate_link",
              comparison_operator: "EQUALS",
              logical_operator: "END",
            },
          ],
        },
      ),

      // 7. Referral link generator API
      n("7", "hitApi", "Generate new referral link for user", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.referralcandy.com/v1/referral_links",
          headers: [
            { key: "Authorization", value: "Bearer {referral_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "advocate_id", value: "{chat_user_id}" },
            { key: "campaign_id", value: "{referral_campaign_id}" },
            { key: "source", value: "chatbot" },
          ],
          variables: [
            {
              name: "referral_link",
              customName: "new_referral_link",
              type: "string",
            },
            {
              name: "referral_code",
              customName: "new_referral_code",
              type: "string",
            },
          ],
        },
      }),

      // 8. Human escalation Slack
      slackNode(
        "8",
        "Escalate to human agent via Slack",
        1480,
        ":hand: *Referral Bot Escalation*\nUser: {chat_user_id} ({chat_user_email})\nIntent: {detected_intent} (confidence: {intent_confidence}%)\nMessage: {user_message}\nBot response: {bot_response_text}\nAction needed: {action_needed}",
        400,
      ),

      // 9. Conversation logger Sheets
      sheetsNode(
        "9",
        "Log conversation to Sheets",
        1720,
        "ReferralBotLogs",
        '[["{timestamp}","{chat_user_id}","{detected_intent}","{intent_confidence}","{user_message}","{bot_response_text}","{action_needed}","{requires_escalation}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("3", "8", "output_2"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "8", "output_2"),
      e("7", "9"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1156. REFERRAL PROGRAM INVITATION AFTER NPS (8 nodes)
  // ===================================================================

  "Referral Program Invitation After NPS": {
    summary:
      "Webhook receives NPS survey responses, AI extracts scores and sentiment, filters promoters (9-10), checks recent referral activity, AI composes personalized invitations, generates unique referral links, sends invitations via SendGrid, and updates CRM analytics in HubSpot.",
    nodes: [
      trigger("Webhook: NPS survey response received", "webhook_app", {
        source: "survey_platform",
        event: "nps.response.submitted",
        howItStarts:
          "A survey platform webhook fires when a customer submits an NPS response, triggering a referral program invitation flow for promoters",
        setup:
          "Configure survey platform webhook (Outgrow Forms, SurveyMonkey, Delighted). Provide OpenAI API key. Configure referral platform API key, SendGrid API key, and HubSpot API key with contacts read/write.",
        inputFields: [],
      }),

      // 2. AI extract NPS data
      aiNode(
        "2",
        "AI: Extract NPS score and customer data",
        280,
        "You are a survey data extraction specialist. Extract the NPS score, customer details, and any qualitative feedback from this webhook payload regardless of field naming conventions. Return JSON.",
        "NPS webhook payload: {webhook_payload}. Extract: respondent_email, respondent_name, nps_score (0-10 integer), qualitative_feedback (verbatim comment or 'none'), survey_id, is_promoter (true if score >= 9).",
        [
          { field: "respondent_email", as: "respondent_email", type: "string" },
          { field: "respondent_name", as: "respondent_name", type: "string" },
          { field: "nps_score", as: "nps_score", type: "number" },
          { field: "qualitative_feedback", as: "nps_feedback", type: "string" },
          { field: "is_promoter", as: "is_promoter", type: "boolean" },
        ],
      ),

      // 3. Promoter filter condition
      n("3", "condition", "Is respondent a promoter (NPS >= 9)?", 520, {
        conditionData: [
          {
            variable: "is_promoter",
            value: "true",
            comparison_operator: "EQUALS",
            logical_operator: "END",
          },
        ],
      }),

      // 4. Recent referral check via HubSpot
      hubspotGetNode(
        "4",
        "Check for recent referral activity in HubSpot",
        760,
        "crm/v3/objects/contacts/{respondent_email}?idProperty=email&properties=last_referral_invite_date,referral_program_status,hs_object_id",
        [
          {
            name: "properties.last_referral_invite_date",
            customName: "last_invite_date",
            type: "string",
          },
          {
            name: "properties.referral_program_status",
            customName: "referral_status",
            type: "string",
          },
          {
            name: "properties.hs_object_id",
            customName: "contact_id",
            type: "string",
          },
        ],
      ),

      // 5. AI invitation composer
      aiNode(
        "5",
        "AI: Compose personalized referral invitation",
        1000,
        "You are a customer engagement specialist. Write a warm, personalized referral program invitation that acknowledges their positive NPS feedback. Make it feel exclusive and appreciative. Return JSON.",
        "Promoter: name={respondent_name}, email={respondent_email}, NPS={nps_score}, feedback={nps_feedback}, referral_status={referral_status}. Generate invitation_subject (warm, personal), invitation_body (HTML email thanking them, quoting their comment if given, explaining referral benefits, clear CTA), preview_text (under 90 chars).",
        [
          {
            field: "invitation_subject",
            as: "invitation_subject",
            type: "string",
          },
          {
            field: "invitation_body",
            as: "invitation_body_html",
            type: "string",
          },
          { field: "preview_text", as: "invitation_preview", type: "string" },
        ],
      ),

      // 6. Referral link generator API
      n("6", "hitApi", "Generate unique referral link", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.referralcandy.com/v1/referral_links",
          headers: [
            { key: "Authorization", value: "Bearer {referral_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "advocate_email", value: "{respondent_email}" },
            { key: "campaign_id", value: "{referral_campaign_id}" },
            { key: "source", value: "nps_promoter" },
          ],
          variables: [
            {
              name: "referral_link",
              customName: "referral_link_url",
              type: "string",
            },
            {
              name: "referral_code",
              customName: "referral_code",
              type: "string",
            },
          ],
        },
      }),

      // 7. SendGrid invitation sender
      n("7", "hitApi", "Send referral invitation via SendGrid", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.sendgrid.com/v3/mail/send",
          headers: [
            { key: "Authorization", value: "Bearer {sendgrid_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "personalizations",
              value:
                '[{"to":[{"email":"{respondent_email}","name":"{respondent_name}"}],"custom_args":{"referral_code":"{referral_code}"}}]',
            },
            {
              key: "from",
              value: '{"email":"{sender_email}","name":"{sender_name}"}',
            },
            { key: "subject", value: "{invitation_subject}" },
            {
              key: "content",
              value: '[{"type":"text/html","value":"{invitation_body_html}"}]',
            },
          ],
          variables: [
            { name: "statusCode", customName: "email_status", type: "number" },
          ],
        },
      }),

      // 8. CRM analytics update HubSpot
      hubspotPostNode(
        "8",
        "Update CRM with referral invitation data",
        1720,
        "crm/v3/objects/contacts/{contact_id}",
        [
          {
            key: "properties",
            value:
              '{"last_referral_invite_date":"{timestamp}","referral_program_status":"invited","nps_score":"{nps_score}","referral_code":"{referral_code}","referral_invite_source":"nps_survey"}',
          },
        ],
        [{ name: "id", customName: "updated_contact_id", type: "string" }],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("3", "8", "output_2"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1157. REFERRAL PROGRAM JOIN CAPTURE (8 nodes)
  // ===================================================================

  "Referral Program Join Capture": {
    summary:
      "Webhook captures form submissions for referral program signups, AI extracts signup data, validates inputs in runCode, checks for duplicates, creates referral accounts via API, sends welcome email and SMS, and enrolls the new advocate in HubSpot CRM.",
    nodes: [
      trigger("Webhook: Referral program signup form", "webhook_form", {
        source: "website_form",
        event: "form.submitted",
        howItStarts:
          "A website form webhook fires when a customer signs up for the referral program, triggering account creation and welcome onboarding",
        setup:
          "Configure form webhook (Outgrow Forms, custom form handler). Provide referral platform API key. Configure OpenAI API key, email sender, SMS provider (Twilio), and HubSpot API key.",
        inputFields: [],
      }),

      // 2. AI extract signup data
      aiNode(
        "2",
        "AI: Extract referral signup data",
        280,
        "You are a form data extraction specialist. Extract referral program signup information from this webhook payload regardless of field naming format. Normalize phone numbers and validate email format. Return JSON.",
        "Form submission payload: {webhook_payload}. Extract: signup_email, signup_name (first and last combined), signup_phone (normalized with country code), company_name (or 'individual'), referral_source (how they heard about the program), agreed_to_terms (true/false).",
        [
          { field: "signup_email", as: "signup_email", type: "string" },
          { field: "signup_name", as: "signup_name", type: "string" },
          { field: "signup_phone", as: "signup_phone", type: "string" },
          { field: "company_name", as: "signup_company", type: "string" },
          { field: "referral_source", as: "referral_source", type: "string" },
          { field: "agreed_to_terms", as: "terms_agreed", type: "boolean" },
        ],
      ),

      // 3. Input validation runCode
      n("3", "runCode", "Validate signup inputs and normalize data", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `const email = vars.signup_email || "";\nconst name = vars.signup_name || "";\nconst phone = vars.signup_phone || "";\nconst termsAgreed = vars.terms_agreed === "true" || vars.terms_agreed === true;\nconst errors = [];\nif (!email.includes("@") || !email.includes(".")) errors.push("Invalid email");\nif (name.trim().length < 2) errors.push("Name too short");\nif (!termsAgreed) errors.push("Terms not accepted");\nconst isValid = errors.length === 0;\nconst cleanPhone = phone.replace(/[^+\\d]/g, "");\nreturn {\n  validation_passed: isValid,\n  validation_errors: errors.length > 0 ? errors.join(", ") : "none",\n  clean_email: email.toLowerCase().trim(),\n  clean_name: name.trim(),\n  clean_phone: cleanPhone,\n  has_phone: cleanPhone.length >= 10\n};`,
          outputVariables: [
            { name: "validation_passed", type: "boolean" },
            { name: "validation_errors", type: "string" },
            { name: "clean_email", type: "string" },
            { name: "clean_name", type: "string" },
            { name: "clean_phone", type: "string" },
            { name: "has_phone", type: "boolean" },
          ],
        },
      }),

      // 4. Duplicate handler condition
      n("4", "condition", "Is signup data valid (no validation errors)?", 760, {
        conditionData: [
          {
            variable: "validation_passed",
            value: "true",
            comparison_operator: "EQUALS",
            logical_operator: "END",
          },
        ],
      }),

      // 5. Referral account creator API
      n("5", "hitApi", "Create referral program account", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.referralcandy.com/v1/advocates",
          headers: [
            { key: "Authorization", value: "Bearer {referral_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "email", value: "{clean_email}" },
            { key: "name", value: "{clean_name}" },
            { key: "source", value: "{referral_source}" },
          ],
          variables: [
            { name: "advocate.id", customName: "advocate_id", type: "string" },
            {
              name: "advocate.referral_link",
              customName: "new_referral_link",
              type: "string",
            },
            {
              name: "advocate.referral_code",
              customName: "new_referral_code",
              type: "string",
            },
          ],
        },
      }),

      // 6. Welcome email sender
      emailNode(
        "6",
        "Send welcome email with referral link",
        1240,
        "{clean_email}",
        "Welcome to our referral program, {clean_name}! Here's your unique link",
        "<h2>Welcome, {clean_name}!</h2><p>You're in! Here's everything you need:</p><h3>Your Referral Link</h3><p><a href='{new_referral_link}'>{new_referral_link}</a></p><p><strong>Code:</strong> {new_referral_code}</p><h3>How It Works</h3><ol><li>Share your link with friends</li><li>They get a discount on their first purchase</li><li>You earn rewards for every successful referral</li></ol>",
      ),

      // 7. SMS welcome sender
      smsNode(
        "7",
        "Send SMS welcome with referral code",
        1240,
        "{clean_phone}",
        "Welcome to our referral program, {clean_name}! Your code: {new_referral_code}. Share: {new_referral_link} - Earn rewards for every friend who joins!",
        400,
      ),

      // 8. CRM enrollment logger HubSpot
      hubspotPostNode(
        "8",
        "Enroll new advocate in HubSpot CRM",
        1480,
        "crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"email":"{clean_email}","firstname":"{clean_name}","phone":"{clean_phone}","company":"{signup_company}","referral_program_status":"active","referral_code":"{new_referral_code}","referral_source":"{referral_source}","referral_signup_date":"{timestamp}"}',
          },
        ],
        [{ name: "id", customName: "hubspot_contact_id", type: "string" }],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "8", "output_2"),
      e("5", "6"),
      e("5", "7"),
      e("6", "8"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1158. REFERRAL REMINDER, NO SHARE (7 nodes)
  // ===================================================================

  "Referral Reminder, No Share": {
    summary:
      "Weekly schedule identifies referral program members who have not shared their links, checks engagement history and timing, AI composes personalized reminders, sends reminder emails, and tracks follow-up activity in Sheets.",
    nodes: [
      trigger("Schedule: Weekly inactive referrer reminder scan", "schedule", {
        schedule: "0 11 * * 3 (Wednesday at 11 AM)",
        howItStarts:
          "A weekly schedule scans for referral program members who joined but have never shared their referral link, sending targeted reminders to encourage first shares",
        setup:
          "Configure HubSpot API key with contacts read access and properties: referral_program_status, referral_shares_count, referral_signup_date. Provide OpenAI API key and Google Sheets ID.",
        inputFields: [],
      }),

      // 2. HubSpot get inactive referrers
      hubspotGetNode(
        "2",
        "Fetch referrers with zero shares from HubSpot",
        280,
        'crm/v3/objects/contacts?properties=email,firstname,phone,referral_code,referral_shares_count,referral_signup_date,hs_object_id&filterGroups=[{"filters":[{"propertyName":"referral_program_status","operator":"EQ","value":"active"},{"propertyName":"referral_shares_count","operator":"EQ","value":"0"}]}]&limit=50',
        [
          {
            name: "results",
            customName: "inactive_referrers_json",
            type: "string",
          },
          {
            name: "total",
            customName: "inactive_referrer_count",
            type: "number",
          },
        ],
      ),

      // 3. Engagement history condition
      n("3", "condition", "Are there inactive referrers to remind?", 520, {
        conditionData: [
          {
            variable: "inactive_referrer_count",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "END",
          },
        ],
      }),

      // 4. Reminder timing gate condition
      n("4", "condition", "Have at least 3 days passed since signup?", 760, {
        conditionData: [
          {
            variable: "inactive_referrer_count",
            value: "1",
            comparison_operator: "GREATER THAN OR EQUALS",
            logical_operator: "END",
          },
        ],
      }),

      // 5. AI reminder composer
      aiNode(
        "5",
        "AI: Compose personalized share reminders",
        1000,
        "You are a referral program engagement specialist. Write friendly, non-pushy reminders that help members understand how easy sharing can be. Include a pre-written message they can copy-paste. Return JSON.",
        "Inactive referrers (joined but never shared): {inactive_referrers_json}. Total: {inactive_referrer_count}. Generate reminder_subject (friendly, benefit-focused), reminder_body (HTML with their referral code, pre-written share message, success stories, clear CTA), reminder_sms (under 160 chars, casual nudge).",
        [
          { field: "reminder_subject", as: "reminder_subject", type: "string" },
          { field: "reminder_body", as: "reminder_body_html", type: "string" },
          { field: "reminder_sms", as: "reminder_sms_text", type: "string" },
        ],
      ),

      // 6. Reminder email dispatcher
      emailNode(
        "6",
        "Send share reminder email",
        1240,
        "{referrer_email}",
        "{reminder_subject}",
        "{reminder_body_html}",
      ),

      // 7. Follow-up tracker Sheets
      sheetsNode(
        "7",
        "Log reminder activity to Sheets",
        1480,
        "ReferralReminders",
        '[["{timestamp}","{inactive_referrer_count}","{referrer_email}","{reminder_subject}","no_share_reminder","sent"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("3", "7", "output_2"),
      e("4", "5", "output_1"),
      e("4", "7", "output_2"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1159. REFERRAL REQUEST AUTOMATION (8 nodes)
  // ===================================================================

  "Referral Request Automation": {
    summary:
      "Webhook captures positive customer events, AI extracts event context, checks customer eligibility, classifies segment in runCode, AI personalizes the referral ask, routes by best channel, provisions referral links, and logs attribution data to Sheets.",
    nodes: [
      trigger("Webhook: Positive customer event detected", "webhook_app", {
        source: "event_bus",
        event: "customer.positive_event",
        howItStarts:
          "An event bus webhook fires when a customer performs a positive action (5-star review, usage milestone, support resolution), triggering a well-timed referral request",
        setup:
          "Configure event bus or CRM webhook for positive events. Provide OpenAI API key, referral platform API key, and Google Sheets for attribution tracking.",
        inputFields: [],
      }),

      // 2. AI extract event data
      aiNode(
        "2",
        "AI: Extract positive event context",
        280,
        "You are an event data extraction specialist. Extract the customer event details from this webhook payload, identifying the event type, customer information, and sentiment context regardless of format. Return JSON.",
        "Event webhook payload: {webhook_payload}. Extract: customer_email, customer_name, event_type (review|milestone|support_resolved|purchase|renewal), event_details (description), satisfaction_indicator (high|medium|low), customer_id, preferred_channel (email or sms, default email).",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "event_type", as: "event_type", type: "string" },
          { field: "event_details", as: "event_details", type: "string" },
          {
            field: "satisfaction_indicator",
            as: "satisfaction_level",
            type: "string",
          },
          { field: "customer_id", as: "customer_id", type: "string" },
          {
            field: "preferred_channel",
            as: "preferred_channel",
            type: "string",
          },
        ],
      ),

      // 3. Customer eligibility condition
      n(
        "3",
        "condition",
        "Is customer satisfaction high enough for referral ask?",
        520,
        {
          conditionData: [
            {
              variable: "satisfaction_level",
              value: "high",
              comparison_operator: "EQUALS",
              logical_operator: "END",
            },
          ],
        },
      ),

      // 4. Segment classifier runCode
      n(
        "4",
        "runCode",
        "Classify customer segment for referral messaging",
        760,
        {
          runCodeDetails: {
            language: "javascript",
            code: `const eventType = vars.event_type || "unknown";\nconst segmentMap = {\n  review: { segment: "advocate", incentive: "double_reward", message_tone: "grateful" },\n  milestone: { segment: "power_user", incentive: "bonus_points", message_tone: "celebratory" },\n  support_resolved: { segment: "recovered", incentive: "standard", message_tone: "appreciative" },\n  purchase: { segment: "buyer", incentive: "percentage", message_tone: "excited" },\n  renewal: { segment: "loyal", incentive: "tier_upgrade", message_tone: "exclusive" }\n};\nconst c = segmentMap[eventType] || segmentMap.purchase;\nreturn { customer_segment: c.segment, incentive_type: c.incentive, message_tone: c.message_tone };`,
            outputVariables: [
              { name: "customer_segment", type: "string" },
              { name: "incentive_type", type: "string" },
              { name: "message_tone", type: "string" },
            ],
          },
        },
      ),

      // 5. AI message personalizer
      aiNode(
        "5",
        "AI: Personalize referral request message",
        1000,
        "You are a customer engagement specialist skilled at timing referral asks perfectly. Write a referral request that feels natural in the context of their recent positive experience. Never be pushy. Return JSON.",
        "Customer: {customer_name} ({customer_email}). Event: {event_type} - {event_details}. Segment: {customer_segment}. Tone: {message_tone}. Incentive: {incentive_type}. Generate referral_subject (contextual to their event), referral_body (HTML email acknowledging event, transitioning to referral ask, explaining incentive, clear CTA), referral_sms (under 160 chars).",
        [
          { field: "referral_subject", as: "referral_subject", type: "string" },
          { field: "referral_body", as: "referral_body_html", type: "string" },
          { field: "referral_sms", as: "referral_sms_text", type: "string" },
        ],
      ),

      // 6. Multi-channel dispatcher condition
      n("6", "condition", "Does customer prefer email over SMS?", 1240, {
        conditionData: [
          {
            variable: "preferred_channel",
            value: "email",
            comparison_operator: "EQUALS",
            logical_operator: "END",
          },
        ],
      }),

      // 7. Referral link provisioner API
      n("7", "hitApi", "Provision referral tracking link", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.referralcandy.com/v1/referral_links",
          headers: [
            { key: "Authorization", value: "Bearer {referral_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "advocate_email", value: "{customer_email}" },
            { key: "campaign_id", value: "{referral_campaign_id}" },
            { key: "source", value: "{event_type}" },
            { key: "incentive_type", value: "{incentive_type}" },
          ],
          variables: [
            {
              name: "referral_link",
              customName: "provisioned_link",
              type: "string",
            },
            {
              name: "referral_code",
              customName: "provisioned_code",
              type: "string",
            },
          ],
        },
      }),

      // 8. Attribution logger Sheets
      sheetsNode(
        "8",
        "Log referral request attribution to Sheets",
        1720,
        "ReferralRequests",
        '[["{timestamp}","{customer_email}","{event_type}","{customer_segment}","{incentive_type}","{preferred_channel}","{provisioned_code}","sent"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("3", "8", "output_2"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "7", "output_2"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1160. REFERRAL REQUEST CAMPAIGN (9 nodes)
  // ===================================================================

  "Referral Request Campaign": {
    summary:
      "Scheduled campaign builds an audience from HubSpot, assigns A/B test variants via runCode, AI generates variant-specific content, sends first touchpoint email, waits 3 days, checks engagement, sends second touchpoint to non-openers, and tracks conversions in Sheets.",
    nodes: [
      trigger("Schedule: Monthly referral request campaign", "schedule", {
        schedule: "0 9 1 * * (1st of month at 9 AM)",
        howItStarts:
          "A monthly schedule launches a multi-touch referral request campaign with A/B testing, targeting eligible customers with sequenced outreach",
        setup:
          "Configure HubSpot API key with contacts read access. Provide OpenAI API key and Google Sheets for conversion tracking.",
        inputFields: [],
      }),

      // 2. HubSpot audience builder
      hubspotGetNode(
        "2",
        "Build referral campaign audience from HubSpot",
        280,
        'crm/v3/objects/contacts?properties=email,firstname,total_revenue,nps_score,referral_program_status,email_engagement_score,hs_object_id&filterGroups=[{"filters":[{"propertyName":"total_revenue","operator":"GTE","value":"200"},{"propertyName":"referral_program_status","operator":"NEQ","value":"active"}]}]&limit=100',
        [
          { name: "results", customName: "audience_json", type: "string" },
          { name: "total", customName: "audience_size", type: "number" },
        ],
      ),

      // 3. A/B variant assigner runCode
      n("3", "runCode", "Assign A/B test variants to audience", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `const audience = JSON.parse(vars.audience_json || "[]");\nconst assigned = audience.map((contact, idx) => {\n  const props = contact.properties || contact;\n  return {\n    email: props.email,\n    name: props.firstname || "there",\n    nps: parseInt(props.nps_score) || 0,\n    revenue: parseFloat(props.total_revenue) || 0,\n    variant: idx % 2 === 0 ? "A" : "B",\n    contact_id: props.hs_object_id\n  };\n});\nconst variantA = assigned.filter(c => c.variant === "A");\nconst variantB = assigned.filter(c => c.variant === "B");\nreturn {\n  assigned_audience_json: JSON.stringify(assigned),\n  variant_a_count: variantA.length,\n  variant_b_count: variantB.length,\n  total_assigned: assigned.length\n};`,
          outputVariables: [
            { name: "assigned_audience_json", type: "string" },
            { name: "variant_a_count", type: "number" },
            { name: "variant_b_count", type: "number" },
            { name: "total_assigned", type: "number" },
          ],
        },
      }),

      // 4. AI content generator
      aiNode(
        "4",
        "AI: Generate A/B variant email content",
        760,
        "You are a referral campaign copywriter. Create two distinct email variants for A/B testing. Variant A uses social proof. Variant B uses exclusivity and limited-time incentives. Return JSON.",
        "Audience: {total_assigned} contacts. Variant A: {variant_a_count}, Variant B: {variant_b_count}. Generate variant_a_subject (social proof angle), variant_a_body (HTML with testimonials, community angle), variant_b_subject (exclusivity angle), variant_b_body (HTML with limited-time bonus, urgency), followup_subject (for non-openers), followup_body (HTML shorter, stronger CTA).",
        [
          {
            field: "variant_a_subject",
            as: "variant_a_subject",
            type: "string",
          },
          {
            field: "variant_a_body",
            as: "variant_a_body_html",
            type: "string",
          },
          {
            field: "variant_b_subject",
            as: "variant_b_subject",
            type: "string",
          },
          {
            field: "variant_b_body",
            as: "variant_b_body_html",
            type: "string",
          },
          { field: "followup_subject", as: "followup_subject", type: "string" },
          { field: "followup_body", as: "followup_body_html", type: "string" },
        ],
      ),

      // 5. Touchpoint 1 email
      emailNode(
        "5",
        "Send Touchpoint 1: A/B variant emails",
        1000,
        "{contact_email}",
        "{variant_a_subject}",
        "{variant_a_body_html}",
      ),

      // 6. Delay 3 days
      n("6", "delayAction", "Wait 3 days before follow-up check", 1240, {
        delayDetails: { duration: 3, unit: "days" },
      }),

      // 7. Engagement check condition
      n("7", "condition", "Did recipient open Touchpoint 1 email?", 1480, {
        conditionData: [
          {
            variable: "total_assigned",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "END",
          },
        ],
      }),

      // 8. Touchpoint 2 email (non-openers)
      emailNode(
        "8",
        "Send Touchpoint 2: Follow-up to non-openers",
        1720,
        "{contact_email}",
        "{followup_subject}",
        "{followup_body_html}",
      ),

      // 9. Conversion tracker Sheets
      sheetsNode(
        "9",
        "Log campaign conversion data to Sheets",
        1960,
        "ReferralCampaigns",
        '[["{timestamp}","{total_assigned}","{variant_a_count}","{variant_b_count}","{variant_a_subject}","{variant_b_subject}","campaign_complete"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "9", "output_1"),
      e("7", "8", "output_2"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1161. REFERRAL REWARD EARNED (7 nodes)
  // ===================================================================

  "Referral Reward Earned": {
    summary:
      "Webhook receives referral conversion events, AI extracts reward details, fetches reward specifics from API, processes fulfillment, sends notification email, logs analytics in HubSpot CRM, and posts a summary to Slack.",
    nodes: [
      trigger("Webhook: Referral conversion completed", "webhook_app", {
        source: "referral_platform",
        event: "referral.converted",
        howItStarts:
          "A referral platform webhook fires when a referred friend converts, triggering reward calculation, fulfillment, and notification to the advocate",
        setup:
          "Configure referral platform webhook for conversion events. Provide OpenAI API key, HubSpot API key with contacts read/write, and Slack webhook for team notifications.",
        inputFields: [],
      }),

      // 2. AI extract reward data
      aiNode(
        "2",
        "AI: Extract conversion and reward data",
        280,
        "You are a referral data extraction specialist. Extract the conversion event details, advocate information, and reward parameters from this webhook payload regardless of format. Return JSON.",
        "Conversion webhook payload: {webhook_payload}. Extract: advocate_email, advocate_name, advocate_id, referred_name, conversion_type (purchase|signup|subscription), conversion_value (number), reward_tier, referral_code.",
        [
          { field: "advocate_email", as: "advocate_email", type: "string" },
          { field: "advocate_name", as: "advocate_name", type: "string" },
          { field: "advocate_id", as: "advocate_id", type: "string" },
          { field: "referred_name", as: "referred_name", type: "string" },
          { field: "conversion_type", as: "conversion_type", type: "string" },
          { field: "conversion_value", as: "conversion_value", type: "number" },
          { field: "referral_code", as: "referral_code", type: "string" },
        ],
      ),

      // 3. Reward details fetcher API
      n("3", "hitApi", "Fetch reward details from referral platform", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.referralcandy.com/v1/rewards?advocate_id={advocate_id}&code={referral_code}",
          headers: [
            { key: "Authorization", value: "Bearer {referral_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "reward.amount",
              customName: "reward_amount",
              type: "string",
            },
            { name: "reward.type", customName: "reward_type", type: "string" },
            {
              name: "reward.description",
              customName: "reward_description",
              type: "string",
            },
            {
              name: "advocate.total_referrals",
              customName: "total_referrals",
              type: "number",
            },
            {
              name: "advocate.total_earned",
              customName: "lifetime_earned",
              type: "string",
            },
          ],
        },
      }),

      // 4. Reward fulfillment processor API
      n("4", "hitApi", "Process reward fulfillment", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.referralcandy.com/v1/rewards/fulfill",
          headers: [
            { key: "Authorization", value: "Bearer {referral_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "advocate_id", value: "{advocate_id}" },
            { key: "reward_amount", value: "{reward_amount}" },
            { key: "reward_type", value: "{reward_type}" },
            { key: "referral_code", value: "{referral_code}" },
          ],
          variables: [
            {
              name: "fulfillment.id",
              customName: "fulfillment_id",
              type: "string",
            },
            {
              name: "fulfillment.status",
              customName: "fulfillment_status",
              type: "string",
            },
          ],
        },
      }),

      // 5. Notification email
      emailNode(
        "5",
        "Send reward earned notification email",
        1000,
        "{advocate_email}",
        "You earned a reward! {referred_name} just converted",
        "<h2>Congratulations, {advocate_name}!</h2><p>Your referral <strong>{referred_name}</strong> just completed a {conversion_type}!</p><h3>Your Reward</h3><p><strong>{reward_description}</strong> - {reward_amount}</p><p>Fulfillment ID: {fulfillment_id}</p><h3>Your Stats</h3><p>Total referrals: {total_referrals} | Lifetime earned: {lifetime_earned}</p>",
      ),

      // 6. CRM analytics logger HubSpot
      hubspotPostNode(
        "6",
        "Update advocate analytics in HubSpot",
        1240,
        "crm/v3/objects/contacts/{advocate_id}",
        [
          {
            key: "properties",
            value:
              '{"last_referral_conversion_date":"{timestamp}","total_referral_conversions":"{total_referrals}","lifetime_referral_earnings":"{lifetime_earned}","last_reward_amount":"{reward_amount}"}',
          },
        ],
        [{ name: "id", customName: "updated_contact_id", type: "string" }],
      ),

      // 7. Slack summary
      slackNode(
        "7",
        "Post referral conversion summary to Slack",
        1480,
        ":tada: *Referral Conversion!*\nAdvocate: {advocate_name} ({advocate_email})\nReferred: {referred_name}\nConversion: {conversion_type} (${conversion_value})\nReward: {reward_amount} ({reward_type})\nTotal referrals: {total_referrals}\nFulfillment: {fulfillment_status}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1162. REFERRAL SOURCE-SPECIFIC NURTURE (9 nodes)
  // ===================================================================

  "Referral Source-Specific Nurture": {
    summary:
      "Webhook captures new leads, AI extracts source information, routes by acquisition channel (organic, paid social, partner), sends source-tailored nurture emails, scores engagement via runCode, evaluates sales-readiness, and creates CRM tasks in HubSpot for qualified leads.",
    nodes: [
      trigger("Webhook: New lead captured", "webhook_app", {
        source: "lead_capture",
        event: "lead.created",
        howItStarts:
          "A lead capture webhook fires when a new lead is created from any source, triggering source-specific nurture sequences",
        setup:
          "Configure lead capture webhook. Provide OpenAI API key. Configure HubSpot API key with contacts and tasks create access.",
        inputFields: [],
      }),

      // 2. AI extract lead source
      aiNode(
        "2",
        "AI: Extract lead data and acquisition source",
        280,
        "You are a lead data extraction specialist. Extract the lead details and determine their acquisition source from UTM parameters, referral data, or form fields regardless of format. Return JSON.",
        "Lead capture payload: {webhook_payload}. Extract: lead_email, lead_name, lead_company, lead_source (organic_search|paid_social|partner_referral|direct|other), utm_campaign, landing_page_url, lead_phone (if available).",
        [
          { field: "lead_email", as: "lead_email", type: "string" },
          { field: "lead_name", as: "lead_name", type: "string" },
          { field: "lead_company", as: "lead_company", type: "string" },
          { field: "lead_source", as: "lead_source", type: "string" },
          { field: "utm_campaign", as: "utm_campaign", type: "string" },
          { field: "landing_page_url", as: "landing_page", type: "string" },
        ],
      ),

      // 3. Source classifier condition
      n("3", "condition", "Is lead from organic search?", 520, {
        conditionData: [
          {
            variable: "lead_source",
            value: "organic_search",
            comparison_operator: "EQUALS",
            logical_operator: "END",
          },
        ],
      }),

      // 4. Organic path email
      emailNode(
        "4",
        "Send organic search nurture email",
        760,
        "{lead_email}",
        "Thanks for finding us, {lead_name} — here's what caught your eye",
        "<h2>Welcome, {lead_name}!</h2><p>You found us through search — you're actively looking for a solution.</p><p>Based on <strong>{landing_page}</strong>, here are relevant resources:</p><ul><li>Product overview matching your search intent</li><li>Case study from a company similar to {lead_company}</li><li>Free trial with guided onboarding</li></ul><p>What challenge brought you here? Reply and let us know.</p>",
      ),

      // 5. Paid social path email
      emailNode(
        "5",
        "Send paid social nurture email",
        760,
        "{lead_email}",
        "{lead_name}, here's the full story behind that ad",
        "<h2>Hey {lead_name}!</h2><p>Thanks for clicking through from our campaign ({utm_campaign}). Here's the deeper dive:</p><ul><li>Detailed product walkthrough video</li><li>ROI calculator for {lead_company}</li><li>Exclusive offer for campaign respondents</li></ul><p>Ready to see it in action? Book a demo.</p>",
        400,
      ),

      // 6. Partner path email
      emailNode(
        "6",
        "Send partner referral nurture email",
        760,
        "{lead_email}",
        "{lead_name}, your partner connection unlocks special benefits",
        "<h2>Welcome, {lead_name}!</h2><p>You were referred through a trusted partner — VIP treatment from day one.</p><ul><li>Extended 30-day free trial</li><li>Priority onboarding support</li><li>Partner-exclusive pricing</li></ul><p>Our partner success team will reach out within 24 hours.</p>",
        720,
      ),

      // 7. Engagement scoring runCode
      n("7", "runCode", "Calculate lead engagement score", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `const source = vars.lead_source || "other";\nconst hasCompany = (vars.lead_company || "").length > 1;\nconst hasPhone = (vars.lead_phone || "").length > 5;\nconst hasUtm = (vars.utm_campaign || "").length > 0;\nlet score = 0;\nconst sourceScores = { organic_search: 35, partner_referral: 40, paid_social: 25, direct: 20, other: 10 };\nscore += sourceScores[source] || 10;\nif (hasCompany) score += 20;\nif (hasPhone) score += 15;\nif (hasUtm) score += 10;\nreturn {\n  engagement_score: score,\n  is_sales_qualified: score >= 50,\n  score_breakdown: "source(" + (sourceScores[source] || 10) + ") + company(" + (hasCompany ? 20 : 0) + ") + phone(" + (hasPhone ? 15 : 0) + ") + utm(" + (hasUtm ? 10 : 0) + ")"\n};`,
          outputVariables: [
            { name: "engagement_score", type: "number" },
            { name: "is_sales_qualified", type: "boolean" },
            { name: "score_breakdown", type: "string" },
          ],
        },
      }),

      // 8. Sales handoff condition
      n("8", "condition", "Is lead sales-qualified (score >= 50)?", 1240, {
        conditionData: [
          {
            variable: "is_sales_qualified",
            value: "true",
            comparison_operator: "EQUALS",
            logical_operator: "END",
          },
        ],
      }),

      // 9. CRM task creator HubSpot
      hubspotPostNode(
        "9",
        "Create sales follow-up task in HubSpot",
        1480,
        "crm/v3/objects/tasks",
        [
          {
            key: "properties",
            value:
              '{"hs_task_subject":"Follow up with {lead_name} from {lead_source}","hs_task_body":"Lead score: {engagement_score}. Source: {lead_source}. Company: {lead_company}. Score: {score_breakdown}. Landing page: {landing_page}.","hs_task_priority":"HIGH","hs_task_status":"NOT_STARTED","hs_timestamp":"{timestamp}"}',
          },
        ],
        [{ name: "id", customName: "task_id", type: "string" }],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("3", "5", "output_2"),
      e("4", "7"),
      e("5", "7"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9", "output_1"),
      e("8", "9", "output_2"),
    ],
  },

  // ===================================================================
  // 1163. REGION-SPECIFIC COMPLIANCE RE-CONSENT (9 nodes)
  // ===================================================================

  "Region-Specific Compliance Re-Consent": {
    summary:
      "Manual trigger initiates a region-targeted re-consent campaign, builds audience segments from HubSpot, checks consent status, AI generates region-specific content (GDPR/CCPA/LGPD), sends re-consent emails, waits 14 days, processes responses, suppresses non-responders, and logs compliance audits to Sheets.",
    nodes: [
      trigger(
        "Manual: Launch regional compliance re-consent campaign",
        "manual",
        {
          howItStarts:
            "A compliance officer initiates a region-specific re-consent campaign to ensure ongoing regulatory compliance for contacts in targeted jurisdictions",
          setup:
            "Configure HubSpot API key with contacts read/write access and properties: region, consent_status, consent_date, gdpr_basis, email. Provide OpenAI API key and Google Sheets for compliance audit trail.",
          inputFields: [
            { name: "target_region", type: "string", example: "EU" },
            { name: "regulation", type: "string", example: "GDPR" },
            { name: "consent_deadline_days", type: "number", example: "30" },
          ],
        },
      ),

      // 2. HubSpot regional segment builder
      hubspotGetNode(
        "2",
        "Build regional contact segment from HubSpot",
        280,
        'crm/v3/objects/contacts?properties=email,firstname,region,consent_status,consent_date,gdpr_basis,hs_object_id&filterGroups=[{"filters":[{"propertyName":"region","operator":"EQ","value":"{target_region}"}]}]&limit=100',
        [
          {
            name: "results",
            customName: "regional_contacts_json",
            type: "string",
          },
          {
            name: "total",
            customName: "regional_contact_count",
            type: "number",
          },
        ],
      ),

      // 3. Consent status checker condition
      n("3", "condition", "Are there contacts needing re-consent?", 520, {
        conditionData: [
          {
            variable: "regional_contact_count",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "END",
          },
        ],
      }),

      // 4. AI region-specific content generator
      aiNode(
        "4",
        "AI: Generate region-specific re-consent content",
        760,
        "You are a privacy compliance specialist fluent in GDPR, CCPA, LGPD, and PIPL regulations. Generate legally compliant re-consent email content. Include proper legal language while remaining clear and human-readable. Return JSON.",
        "Region: {target_region}. Regulation: {regulation}. Contacts: {regional_contact_count}. Deadline: {consent_deadline_days} days. Generate reconsent_subject (clear, compliant), reconsent_body (HTML with regulation-specific language, data processing purposes, explicit yes/no consent buttons, privacy policy link, deadline), legal_disclaimer (footer text), compliance_reference (regulation articles referenced).",
        [
          {
            field: "reconsent_subject",
            as: "reconsent_subject",
            type: "string",
          },
          {
            field: "reconsent_body",
            as: "reconsent_body_html",
            type: "string",
          },
          { field: "legal_disclaimer", as: "legal_disclaimer", type: "string" },
          {
            field: "compliance_reference",
            as: "compliance_reference",
            type: "string",
          },
        ],
      ),

      // 5. Re-consent email sender
      emailNode(
        "5",
        "Send re-consent request email",
        1000,
        "{contact_email}",
        "{reconsent_subject}",
        "{reconsent_body_html}<br/><hr/><small>{legal_disclaimer}</small>",
      ),

      // 6. Delay 14 days
      n("6", "delayAction", "Wait 14 days for consent responses", 1240, {
        delayDetails: { duration: 14, unit: "days" },
      }),

      // 7. Consent response processor condition
      n("7", "condition", "Did contact provide re-consent response?", 1480, {
        conditionData: [
          {
            variable: "regional_contact_count",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "END",
          },
        ],
      }),

      // 8. Non-responder suppression HubSpot
      hubspotPostNode(
        "8",
        "Suppress non-responding contacts in HubSpot",
        1720,
        "crm/v3/objects/contacts/batch/update",
        [
          {
            key: "inputs",
            value:
              '[{"id":"{contact_id}","properties":{"consent_status":"suppressed","suppression_reason":"{regulation}_non_response","suppression_date":"{timestamp}","email_opt_out":"true"}}]',
          },
        ],
        [
          { name: "results", customName: "suppressed_results", type: "string" },
          { name: "status", customName: "suppression_status", type: "string" },
        ],
        400,
      ),

      // 9. Compliance audit logger Sheets
      sheetsNode(
        "9",
        "Log compliance audit trail to Sheets",
        1960,
        "ComplianceAudit",
        '[["{timestamp}","{target_region}","{regulation}","{regional_contact_count}","{compliance_reference}","{reconsent_subject}","campaign_sent"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("3", "9", "output_2"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "9", "output_1"),
      e("7", "8", "output_2"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1164. REPERMISSION CAMPAIGN (8 nodes)
  // ===================================================================

  "Repermission Campaign": {
    summary:
      "Scheduled scan identifies inactive subscribers from HubSpot, evaluates engagement history, AI generates re-engagement content, sends repermission emails, waits 7 days, processes confirmations, and removes non-responders from active lists in HubSpot.",
    nodes: [
      trigger("Schedule: Quarterly repermission campaign", "schedule", {
        schedule: "0 8 1 */3 * (1st of quarter at 8 AM)",
        howItStarts:
          "A quarterly schedule launches a repermission campaign targeting subscribers who have not engaged with emails in over 90 days",
        setup:
          "Configure HubSpot API key with contacts read/write and list management. Properties: last_email_open_date, email_engagement_score, subscription_status. Provide OpenAI API key.",
        inputFields: [],
      }),

      // 2. HubSpot inactive subscriber scan
      hubspotGetNode(
        "2",
        "Scan for inactive subscribers in HubSpot",
        280,
        'crm/v3/objects/contacts?properties=email,firstname,last_email_open_date,email_engagement_score,subscription_status,hs_object_id&filterGroups=[{"filters":[{"propertyName":"last_email_open_date","operator":"LT","value":"{ninety_days_ago}"},{"propertyName":"subscription_status","operator":"EQ","value":"active"}]}]&limit=100',
        [
          {
            name: "results",
            customName: "inactive_subscribers_json",
            type: "string",
          },
          {
            name: "total",
            customName: "inactive_subscriber_count",
            type: "number",
          },
        ],
      ),

      // 3. Engagement history condition
      n(
        "3",
        "condition",
        "Are there inactive subscribers to re-permission?",
        520,
        {
          conditionData: [
            {
              variable: "inactive_subscriber_count",
              value: "0",
              comparison_operator: "GREATER THAN",
              logical_operator: "END",
            },
          ],
        },
      ),

      // 4. AI re-engagement composer
      aiNode(
        "4",
        "AI: Compose repermission email content",
        760,
        "You are an email re-engagement specialist. Write a sincere repermission email giving subscribers a clear choice to stay or unsubscribe. Be honest. Make staying feel valuable but never guilt-trip. Return JSON.",
        "Inactive subscribers: {inactive_subscriber_count}. Generate repermission_subject (honest, respectful, curiosity-driven), repermission_body (HTML: acknowledge inactivity, highlight 3 missed features/content, YES/NO buttons, no-response means unsubscribe), unsubscribe_notice (CANSPAM footer).",
        [
          {
            field: "repermission_subject",
            as: "repermission_subject",
            type: "string",
          },
          {
            field: "repermission_body",
            as: "repermission_body_html",
            type: "string",
          },
          {
            field: "unsubscribe_notice",
            as: "unsubscribe_footer",
            type: "string",
          },
        ],
      ),

      // 5. Repermission email sender
      emailNode(
        "5",
        "Send repermission email to inactive subscribers",
        1000,
        "{subscriber_email}",
        "{repermission_subject}",
        "{repermission_body_html}<br/><hr/><small>{unsubscribe_footer}</small>",
      ),

      // 6. Delay 7 days
      n("6", "delayAction", "Wait 7 days for re-confirmation responses", 1240, {
        delayDetails: { duration: 7, unit: "days" },
      }),

      // 7. Re-confirmation processor condition
      n(
        "7",
        "condition",
        "Did subscriber re-confirm their subscription?",
        1480,
        {
          conditionData: [
            {
              variable: "inactive_subscriber_count",
              value: "0",
              comparison_operator: "GREATER THAN",
              logical_operator: "END",
            },
          ],
        },
      ),

      // 8. Non-responder list cleaner HubSpot
      hubspotPostNode(
        "8",
        "Remove non-responders from active lists",
        1720,
        "crm/v3/objects/contacts/batch/update",
        [
          {
            key: "inputs",
            value:
              '[{"id":"{contact_id}","properties":{"subscription_status":"unsubscribed","unsubscribe_reason":"repermission_no_response","unsubscribe_date":"{timestamp}","email_opt_out":"true"}}]',
          },
        ],
        [
          { name: "results", customName: "cleanup_results", type: "string" },
          { name: "status", customName: "cleanup_status", type: "string" },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("3", "8", "output_2"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
      e("7", "8", "output_2"),
    ],
  },

  // ===================================================================
  // 1165. RESUBSCRIBER WELCOME (8 nodes)
  // ===================================================================

  "Resubscriber Welcome": {
    summary:
      "Webhook captures resubscribe events, AI extracts resubscriber data, fetches historical engagement from HubSpot, classifies the return segment, AI personalizes welcome-back message, sends welcome email, prompts preference center setup, and updates CRM reactivation status.",
    nodes: [
      trigger("Webhook: Contact resubscribed", "webhook_app", {
        source: "email_platform",
        event: "contact.resubscribed",
        howItStarts:
          "An email platform webhook fires when a previously unsubscribed contact resubscribes, triggering a personalized welcome-back experience",
        setup:
          "Configure email platform webhook for resubscribe events. Provide OpenAI API key and HubSpot API key with contacts read/write.",
        inputFields: [],
      }),

      // 2. AI extract resubscriber data
      aiNode(
        "2",
        "AI: Extract resubscriber details",
        280,
        "You are a contact data extraction specialist. Extract the resubscriber information from this webhook payload regardless of field naming format. Return JSON.",
        "Resubscribe webhook payload: {webhook_payload}. Extract: resubscriber_email, resubscriber_name (or 'Subscriber'), resubscribe_source (website|email_link|preference_center|api), previous_unsubscribe_reason (if available or 'unknown').",
        [
          {
            field: "resubscriber_email",
            as: "resubscriber_email",
            type: "string",
          },
          {
            field: "resubscriber_name",
            as: "resubscriber_name",
            type: "string",
          },
          {
            field: "resubscribe_source",
            as: "resubscribe_source",
            type: "string",
          },
          {
            field: "previous_unsubscribe_reason",
            as: "prev_unsub_reason",
            type: "string",
          },
        ],
      ),

      // 3. Historical data fetcher HubSpot
      hubspotGetNode(
        "3",
        "Fetch historical engagement from HubSpot",
        520,
        "crm/v3/objects/contacts/{resubscriber_email}?idProperty=email&properties=email,firstname,total_revenue,num_associated_deals,original_signup_date,lifecycle_stage,hs_object_id",
        [
          {
            name: "properties.total_revenue",
            customName: "historical_revenue",
            type: "string",
          },
          {
            name: "properties.num_associated_deals",
            customName: "past_deals",
            type: "string",
          },
          {
            name: "properties.original_signup_date",
            customName: "original_signup",
            type: "string",
          },
          {
            name: "properties.lifecycle_stage",
            customName: "lifecycle_stage",
            type: "string",
          },
          {
            name: "properties.hs_object_id",
            customName: "contact_id",
            type: "string",
          },
        ],
      ),

      // 4. Segment classifier condition
      n("4", "condition", "Is resubscriber a former paying customer?", 760, {
        conditionData: [
          {
            variable: "historical_revenue",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "END",
          },
        ],
      }),

      // 5. AI welcome personalizer
      aiNode(
        "5",
        "AI: Personalize welcome-back message",
        1000,
        "You are a customer re-engagement specialist. Write a warm welcome-back email acknowledging their return without dwelling on the departure. Focus on what is new. Return JSON.",
        "Resubscriber: {resubscriber_name} ({resubscriber_email}). Source: {resubscribe_source}. Previous unsub reason: {prev_unsub_reason}. Revenue: ${historical_revenue}. Deals: {past_deals}. Original signup: {original_signup}. Stage: {lifecycle_stage}. Generate welcome_subject (warm, forward-looking), welcome_body (HTML: acknowledge return, highlight 3 new features, personalized CTA), preference_subject (preference center prompt), preference_body (HTML: customize email preferences).",
        [
          { field: "welcome_subject", as: "welcome_subject", type: "string" },
          { field: "welcome_body", as: "welcome_body_html", type: "string" },
          {
            field: "preference_subject",
            as: "preference_subject",
            type: "string",
          },
          {
            field: "preference_body",
            as: "preference_body_html",
            type: "string",
          },
        ],
      ),

      // 6. Welcome-back email sender
      emailNode(
        "6",
        "Send welcome-back email",
        1240,
        "{resubscriber_email}",
        "{welcome_subject}",
        "{welcome_body_html}",
      ),

      // 7. Preference center prompt email
      emailNode(
        "7",
        "Send preference center setup prompt",
        1480,
        "{resubscriber_email}",
        "{preference_subject}",
        "{preference_body_html}",
      ),

      // 8. CRM reactivation update HubSpot
      hubspotPostNode(
        "8",
        "Update CRM reactivation status in HubSpot",
        1720,
        "crm/v3/objects/contacts/{contact_id}",
        [
          {
            key: "properties",
            value:
              '{"subscription_status":"active","resubscribe_date":"{timestamp}","resubscribe_source":"{resubscribe_source}","previous_unsubscribe_reason":"{prev_unsub_reason}","lifecycle_stage":"subscriber","email_opt_out":"false"}',
          },
        ],
        [{ name: "id", customName: "updated_contact_id", type: "string" }],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1166. RETARGETING VIDEO AD (9 nodes)
  // ===================================================================

  "Retargeting Video Ad": {
    summary:
      "Webhook captures cart abandonment events, AI extracts cart data, enriches with product details from Shopify, AI generates a video script, renders via Kling video API, validates quality, routes through human approval, uploads to Meta Ads, and monitors performance in Sheets.",
    nodes: [
      trigger("Webhook: Cart abandoned", "webhook_app", {
        source: "ecommerce",
        event: "cart.abandoned",
        howItStarts:
          "An ecommerce webhook fires when a customer abandons their cart, triggering an AI-powered video ad creation pipeline for personalized retargeting",
        setup:
          "Configure ecommerce platform webhook for cart abandonment (30-minute threshold). Provide Shopify API credentials, OpenAI API key, Kling AI video API key, Meta Marketing API access token, and Google Sheets.",
        inputFields: [],
      }),

      // 2. AI extract cart data
      aiNode(
        "2",
        "AI: Extract cart abandonment data",
        280,
        "You are an ecommerce data extraction specialist. Extract cart details, customer information, and product IDs from this webhook payload regardless of format. Return JSON.",
        "Cart abandonment webhook payload: {webhook_payload}. Extract: customer_email, customer_name (or 'Shopper'), cart_total (number), currency, product_ids (comma-separated), product_names (comma-separated), cart_url (recovery link if available).",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "cart_total", as: "cart_total", type: "number" },
          { field: "product_ids", as: "product_ids", type: "string" },
          { field: "product_names", as: "product_names", type: "string" },
          { field: "cart_url", as: "cart_recovery_url", type: "string" },
        ],
      ),

      // 3. Product data enricher Shopify API
      n("3", "hitApi", "Enrich product data from Shopify", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://{shopify_store}.myshopify.com/admin/api/2024-01/products.json?ids={product_ids}&fields=id,title,images,variants,body_html",
          headers: [
            { key: "X-Shopify-Access-Token", value: "{shopify_access_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "products",
              customName: "enriched_products_json",
              type: "string",
            },
            {
              name: "products[0].images[0].src",
              customName: "primary_product_image",
              type: "string",
            },
            {
              name: "products[0].variants[0].price",
              customName: "primary_product_price",
              type: "string",
            },
          ],
        },
      }),

      // 4. AI video script generator
      aiNode(
        "4",
        "AI: Generate retargeting video script",
        760,
        "You are a performance marketing video creative director. Write a 15-second video ad script for social media retargeting. Create urgency, showcase the product, drive back to purchase. Return JSON.",
        "Abandoned products: {product_names}. Cart total: ${cart_total}. Products: {enriched_products_json}. Image: {primary_product_image}. Generate video_prompt (detailed Kling AI prompt with visual style, transitions), text_overlays (array of display text), voiceover_text (under 40 words), ad_headline (under 40 chars), ad_description (under 125 chars).",
        [
          {
            field: "video_prompt",
            as: "video_generation_prompt",
            type: "string",
          },
          { field: "text_overlays", as: "text_overlays_json", type: "string" },
          { field: "voiceover_text", as: "voiceover_text", type: "string" },
          { field: "ad_headline", as: "ad_headline", type: "string" },
          { field: "ad_description", as: "ad_description", type: "string" },
        ],
      ),

      // 5. Kling video renderer API
      n("5", "hitApi", "Generate retargeting video via Kling AI", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.klingai.com/v1/videos/text2video",
          headers: [
            { key: "Authorization", value: "Bearer {kling_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "prompt", value: "{video_generation_prompt}" },
            { key: "duration", value: "5" },
            { key: "aspect_ratio", value: "9:16" },
            { key: "model", value: "kling-v1" },
          ],
          variables: [
            {
              name: "data.task_id",
              customName: "video_task_id",
              type: "string",
            },
            {
              name: "data.task_status",
              customName: "video_status",
              type: "string",
            },
            {
              name: "data.works[0].resource.resource",
              customName: "video_url",
              type: "string",
            },
          ],
        },
      }),

      // 6. Video quality condition
      n("6", "condition", "Did video generation complete successfully?", 1240, {
        conditionData: [
          {
            variable: "video_status",
            value: "succeed",
            comparison_operator: "EQUALS",
            logical_operator: "END",
          },
        ],
      }),

      // 7. Human approval gate
      n("7", "approvalGate", "Creative team approves retargeting video", 1480, {
        approvalPrompt:
          "Review AI-generated retargeting video:\n\nProducts: {product_names}\nCart: ${cart_total}\nVideo: {video_url}\nHeadline: {ad_headline}\nDescription: {ad_description}\nVoiceover: {voiceover_text}\n\nApprove to upload to Meta Ads.",
      }),

      // 8. Meta Ads uploader API
      n("8", "hitApi", "Upload video ad to Meta Ads Manager", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://graph.facebook.com/v18.0/act_{ad_account_id}/adcreatives",
          headers: [
            { key: "Authorization", value: "Bearer {meta_access_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "name", value: "Retarget - {product_names} - {timestamp}" },
            {
              key: "object_story_spec",
              value:
                '{"page_id":"{page_id}","video_data":{"video_url":"{video_url}","title":"{ad_headline}","message":"{ad_description}","call_to_action":{"type":"SHOP_NOW","value":{"link":"{cart_recovery_url}"}}}}',
            },
          ],
          variables: [
            { name: "id", customName: "ad_creative_id", type: "string" },
          ],
        },
      }),

      // 9. Performance monitor Sheets
      sheetsNode(
        "9",
        "Log video ad performance data to Sheets",
        1960,
        "RetargetingAds",
        '[["{timestamp}","{customer_email}","{product_names}","{cart_total}","{video_task_id}","{ad_creative_id}","{ad_headline}","uploaded"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "9", "output_2"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1167. REVIEW COLLECTION BOT (9 nodes)
  // ===================================================================

  "Review Collection Bot": {
    summary:
      "Webhook receives delivery confirmation events, AI extracts order data, waits 3 days for product experience, sends review request email, AI analyzes responses conversationally, routes by sentiment (positive to publisher, negative to Slack), and aggregates analytics to Sheets.",
    nodes: [
      trigger("Webhook: Order delivered", "webhook_app", {
        source: "shipping_platform",
        event: "delivery.confirmed",
        howItStarts:
          "A shipping platform webhook fires when a customer order is confirmed delivered, initiating a timed review collection sequence with sentiment routing",
        setup:
          "Configure shipping webhook (ShipStation, Shopify, AfterShip) for delivery confirmation. Provide OpenAI API key. Set up review platform API (Trustpilot). Configure Slack webhook and Google Sheets.",
        inputFields: [],
      }),

      // 2. AI extract order data
      aiNode(
        "2",
        "AI: Extract order and delivery data",
        280,
        "You are an order data extraction specialist. Extract delivery confirmation details from this webhook payload regardless of field naming format. Return JSON.",
        "Delivery webhook payload: {webhook_payload}. Extract: customer_email, customer_name (or 'Customer'), order_id, product_names (comma-separated), delivery_date, order_total (number).",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "order_id", as: "order_id", type: "string" },
          { field: "product_names", as: "delivered_products", type: "string" },
          { field: "delivery_date", as: "delivery_date", type: "string" },
          { field: "order_total", as: "order_total", type: "number" },
        ],
      ),

      // 3. Delay 3 days
      n("3", "delayAction", "Wait 3 days for product experience", 520, {
        delayDetails: { duration: 3, unit: "days" },
      }),

      // 4. Review request email
      emailNode(
        "4",
        "Send review request email",
        760,
        "{customer_email}",
        "How are you enjoying your {delivered_products}, {customer_name}?",
        "<h2>Hi {customer_name}!</h2><p>Your order <strong>#{order_id}</strong> was delivered on {delivery_date}. We hope you love your <strong>{delivered_products}</strong>!</p><p>Your feedback helps other shoppers and helps us improve.</p><p><strong>Rate your purchase:</strong></p><p><a href='{review_link}&rating=5'>Excellent</a> | <a href='{review_link}&rating=4'>Good</a> | <a href='{review_link}&rating=3'>Okay</a> | <a href='{review_link}&rating=2'>Poor</a> | <a href='{review_link}&rating=1'>Terrible</a></p><p>Or simply reply with your thoughts!</p>",
      ),

      // 5. AI conversational collector
      aiNode(
        "5",
        "AI: Analyze customer review response",
        1000,
        "You are a review analysis specialist. Analyze the customer review response to extract sentiment, rating, and key themes. Determine publishability and escalation needs. Return JSON.",
        "Customer: {customer_name}. Products: {delivered_products}. Order: ${order_total}. Response data: {webhook_payload}. Return: sentiment (positive|neutral|negative), star_rating (1-5), review_text (cleaned feedback), key_themes (comma-separated), is_publishable (true if positive and no profanity/private info), escalation_needed (true if negative).",
        [
          { field: "sentiment", as: "review_sentiment", type: "string" },
          { field: "star_rating", as: "review_rating", type: "number" },
          { field: "review_text", as: "cleaned_review_text", type: "string" },
          { field: "key_themes", as: "review_themes", type: "string" },
          { field: "is_publishable", as: "is_publishable", type: "boolean" },
          {
            field: "escalation_needed",
            as: "needs_escalation",
            type: "boolean",
          },
        ],
      ),

      // 6. Sentiment analyzer condition
      n("6", "condition", "Is review positive and publishable?", 1240, {
        conditionData: [
          {
            variable: "is_publishable",
            value: "true",
            comparison_operator: "EQUALS",
            logical_operator: "AND",
          },
          {
            variable: "review_rating",
            value: "3",
            comparison_operator: "GREATER THAN",
            logical_operator: "END",
          },
        ],
      }),

      // 7. Public review publisher API
      n("7", "hitApi", "Publish positive review to Trustpilot", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.trustpilot.com/v1/private/business-units/{business_unit_id}/reviews",
          headers: [
            { key: "Authorization", value: "Bearer {trustpilot_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "consumerEmail", value: "{customer_email}" },
            { key: "consumerName", value: "{customer_name}" },
            { key: "referenceId", value: "{order_id}" },
            { key: "stars", value: "{review_rating}" },
            { key: "text", value: "{cleaned_review_text}" },
          ],
          variables: [
            { name: "id", customName: "review_id", type: "string" },
            {
              name: "status",
              customName: "review_publish_status",
              type: "string",
            },
          ],
        },
      }),

      // 8. Negative review escalator Slack
      slackNode(
        "8",
        "Escalate negative review to customer success",
        1480,
        ":warning: *Negative Review Alert*\nCustomer: {customer_name} ({customer_email})\nOrder: #{order_id} - {delivered_products}\nRating: {review_rating}/5\nSentiment: {review_sentiment}\nThemes: {review_themes}\n\n> {cleaned_review_text}\n\nPlease reach out within 2 hours.",
        400,
      ),

      // 9. Analytics aggregator Sheets
      sheetsNode(
        "9",
        "Log review analytics to Sheets",
        1720,
        "ReviewCollection",
        '[["{timestamp}","{customer_email}","{order_id}","{delivered_products}","{review_rating}","{review_sentiment}","{review_themes}","{is_publishable}","{needs_escalation}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "8", "output_2"),
      e("7", "9"),
      e("8", "9"),
    ],
  },
};
